import { apiClient } from './apiClient';
import { UserAccessContext } from '../types/UserAccessContext';

export async function fetchUserAccess(): Promise<UserAccessContext> {
  const response = await apiClient.get<UserAccessContext>('/v1/me/access');
  return normalizeUserAccess(response.data);
}

export type AppUserManagement = {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  roleIds: string[];
  roleCodes: string[];
  roleNames: string[];
  accessProfileId?: string | null;
  permissionGroupIds: string[];
  permissionGroupNames: string[];
  jobTitle?: string | null;
  teamNames: string[];
  managerNames: string[];
};

type AppUserManagementResponse = Partial<AppUserManagement> & {
  roleId?: string;
  roleCode?: string;
  roleName?: string;
};

function normalizeAppUser(raw: AppUserManagementResponse): AppUserManagement {
  const roleIds = raw.roleIds?.length
    ? raw.roleIds
    : raw.roleId
      ? [raw.roleId]
      : [];

  const roleNames = raw.roleNames?.length
    ? raw.roleNames
    : raw.roleName
      ? [raw.roleName]
      : [];

  const roleCodes = raw.roleCodes?.length
    ? raw.roleCodes
    : raw.roleCode
      ? [raw.roleCode]
      : [];

  return {
    id: raw.id ?? '',
    name: raw.name ?? '',
    email: raw.email ?? '',
    isActive: raw.isActive ?? true,
    roleIds,
    roleNames,
    roleCodes,
    accessProfileId: raw.accessProfileId ?? roleIds[0] ?? null,
    permissionGroupIds: raw.permissionGroupIds ?? [],
    permissionGroupNames: raw.permissionGroupNames ?? [],
    jobTitle: raw.jobTitle,
    teamNames: raw.teamNames ?? [],
    managerNames: raw.managerNames ?? [],
  };
}

function normalizeUserAccess(raw: Partial<UserAccessContext> & { roleId?: string; roleCode?: string }): UserAccessContext {
  const roleIds = raw.roleIds?.length
    ? raw.roleIds
    : raw.roleId
      ? [raw.roleId]
      : [];

  const roleCodes = raw.roleCodes?.length
    ? raw.roleCodes
    : raw.roleCode
      ? [raw.roleCode]
      : [];

  return {
    userId: raw.userId ?? '',
    roleIds,
    roleCodes,
    profileCode: raw.profileCode ?? roleCodes[0] ?? null,
    permissionGroupIds: raw.permissionGroupIds ?? [],
    permissionCodes: raw.permissionCodes ?? [],
    modules: raw.modules ?? [],
    isOwnerCustomer: raw.isOwnerCustomer ?? false,
  };
}

export type RoleManagement = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  type: string;
};

export type AccessPermission = {
  id: string;
  code: string;
  name: string;
  description?: string;
  moduleCode: string;
  type: string;
  isActive: boolean;
};

export type SystemModule = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  routePath: string;
  pillar: string;
  sortOrder: number;
  isActive: boolean;
};

export type ListPaginationFilter = {
  page?: number;
  pageSize?: number;
};

export type AppUserListResult = {
  items: AppUserManagement[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type RoleListResult = {
  items: RoleManagement[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type SystemModuleListResult = {
  items: SystemModule[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export async function listModules(filter: ListPaginationFilter = {}): Promise<SystemModuleListResult> {
  const response = await apiClient.get<SystemModuleListResult>('/v1/access-control/modules', { params: filter });
  return response.data;
}

export async function listUsers(filter: ListPaginationFilter = {}): Promise<AppUserListResult> {
  const response = await apiClient.get<AppUserListResult>('/v1/access-control/users', { params: filter });
  return {
    ...response.data,
    items: (response.data.items ?? []).map((item) => normalizeAppUser(item)),
  };
}

export async function createUser(payload: {
  name: string;
  email: string;
  password: string;
  roleIds?: string[];
  accessProfileId?: string;
  permissionGroupIds?: string[];
  isActive: boolean;
}): Promise<AppUserManagement> {
  const response = await apiClient.post<AppUserManagementResponse>('/v1/access-control/users', payload);
  return normalizeAppUser(response.data);
}

export async function updateUser(
  id: string,
  payload: {
    name: string;
    email: string;
    password?: string;
    roleIds?: string[];
    accessProfileId?: string;
    permissionGroupIds?: string[];
    isActive: boolean;
  },
): Promise<AppUserManagement> {
  const response = await apiClient.put<AppUserManagementResponse>(`/v1/access-control/users/${id}`, payload);
  return normalizeAppUser(response.data);
}

export async function deleteUser(id: string): Promise<void> {
  await apiClient.delete(`/v1/access-control/users/${id}`);
}

export async function listRoles(filter: ListPaginationFilter = {}): Promise<RoleListResult> {
  const response = await apiClient.get<RoleListResult>('/v1/access-control/roles', { params: filter });
  return response.data;
}

export async function createRole(payload: { code: string; name: string; description?: string }): Promise<RoleManagement> {
  const response = await apiClient.post<RoleManagement>('/v1/access-control/roles', payload);
  return response.data;
}

export async function updateRole(
  id: string,
  payload: { code: string; name: string; description?: string },
): Promise<RoleManagement> {
  const response = await apiClient.put<RoleManagement>(`/v1/access-control/roles/${id}`, payload);
  return response.data;
}

export async function deleteRole(id: string): Promise<void> {
  await apiClient.delete(`/v1/access-control/roles/${id}`);
}

export async function listPermissionsCatalog(): Promise<AccessPermission[]> {
  const response = await apiClient.get<AccessPermission[]>('/v1/access-control/modules/permissions');
  return response.data;
}

export async function getRolePermissions(roleId: string): Promise<AccessPermission[]> {
  const response = await apiClient.get<AccessPermission[]>(`/v1/access-control/roles/${roleId}/permissions`);
  return response.data;
}

export async function replaceRolePermissions(roleId: string, permissionIds: string[]): Promise<void> {
  await apiClient.put(`/v1/access-control/roles/${roleId}/permissions`, { permissionIds });
}
